// @ts-check
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import LocationFeatureFlagSelect from './LocationFeatureFlagSelect';

/** @typedef {'allow' | 'deny'} LocationFeatureFlagAction */
/** @typedef {{scope: 'company', companyName?: string, featureFlag: string, description: string} | {scope: 'featureFlag', featureFlag: string, description: string} | null} LocationFeatureFlagSelection */

/**
 * @param {{
 *   selection: LocationFeatureFlagSelection,
 *   action: LocationFeatureFlagAction,
 *   isSaving: boolean,
 *   onActionChange: (action: LocationFeatureFlagAction) => void,
 *   onClose: () => void,
 *   onSubmit: () => void,
 * }} props
 */
const LocationFeatureFlagConfirmDialog = ({ selection, action, isSaving, onActionChange, onClose, onSubmit }) => {
  let scopeConfirmation = null;
  let actionAriaLabel = '';

  if (selection?.scope === 'company') {
    scopeConfirmation = (
      <>
        {'for all locations inside company '}
        <strong>{selection.companyName}</strong>
        {'?'}
      </>
    );
    actionAriaLabel = `${selection.featureFlag} company feature flag action`;
  } else if (selection?.scope === 'featureFlag') {
    scopeConfirmation = 'for all locations?';
    actionAriaLabel = `${selection.featureFlag} featureFlag feature flag action`;
  }

  return (
    <Dialog open={Boolean(selection)} onClose={onClose}>
      <DialogTitle>Change Feature Flag Action</DialogTitle>
      <DialogContent>
        {selection && (
          <>
            <p>
              {'Are you sure you want to change the Action for feature flag '}
              <strong>{selection.featureFlag}</strong> {scopeConfirmation}
              {' This action will take effect immediately.'}
            </p>{' '}
            <br />
            <p>Description: {selection.description}</p> <br />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>Action:</span>
              <LocationFeatureFlagSelect
                value={action}
                featureFlag={selection.featureFlag}
                ariaLabel={actionAriaLabel}
                disabled={isSaving}
                onChange={onActionChange}
              />
            </div>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={onSubmit} disabled={isSaving}>
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

LocationFeatureFlagConfirmDialog.propTypes = {
  selection: PropTypes.shape({
    scope: PropTypes.oneOf(['company', 'featureFlag']).isRequired,
    companyName: PropTypes.string,
    featureFlag: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }),
  action: PropTypes.oneOf(['allow', 'deny']).isRequired,
  isSaving: PropTypes.bool.isRequired,
  onActionChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default LocationFeatureFlagConfirmDialog;
